/**
 * Which device a subscription was bought on (#4096).
 *
 * WHY THIS EXISTS. The `subscription_started` event is written by our server,
 * not by the app running on the person's phone or browser — for all four payment
 * providers. A server-written event carries none of the app-side context flags,
 * so `app_native` (the flag meaning "this happened inside the installed phone
 * app") reads `false` on every subscription start we have ever recorded. Split a
 * funnel on it and Mixpanel, finding no usable value on the first step,
 * re-buckets the people who finished using the platform on their SECOND step.
 * June 2026 produced a split that cannot be true: "native" 49 of 49 people
 * (100%), "web" 15 of 234 (6%), against a real overall figure of 22%.
 *
 * WHY TWO VALUES AND NOT ONE. Buying inside Instagram's in-app browser on an
 * iPhone and buying in Chrome on a Windows desktop are both "web browser"
 * purchases, and telling them apart is the whole point. So the description keeps
 * two independent axes — which shell, and which operating system — mirroring how
 * our client-written events already describe themselves (`app_native` +
 * `app_standalone` + `$os`) rather than inventing a collapsed tag. See
 * `design-docs/telemetry.md`, "Platform fields".
 *
 * WHY IT LIVES IN `shared/`. Both ends need the same answer: the browser
 * resolves its own environment at checkout, and the server resolves what to
 * record. Two copies of these rules would drift, and the drift would be silent —
 * it produces a plausible wrong bucket, never an error.
 */

// The vocabularies are declared ONCE, as arrays, and the types are derived from
// them. Declaring the union separately would let the two drift: a value added to
// the union but not the array still compiles, and the guard below would then
// reject it as `unknown` with no error anywhere — a plausible wrong bucket, which
// is the failure this whole module exists to prevent. `checkout-tracking.ts`
// derives `TrackingKey` the same way, from the keys of its policy table.

/** Which shell the purchase happened in. */
const CLIENT_PLATFORMS = [
  'ios-native',
  'android-native',
  'installed-pwa',
  'web-browser',
  'unknown',
] as const;

/** Which operating system it happened on. */
const CLIENT_OSES = [
  'ios',
  'android',
  'windows',
  'macos',
  'linux',
  'unknown',
] as const;

export type ClientPlatform = typeof CLIENT_PLATFORMS[number];
export type ClientOs = typeof CLIENT_OSES[number];

/** Both answers together. Neither is ever absent — an unknown says so. */
export interface ClientEnvironment {
  platform: ClientPlatform;
  os: ClientOs;
}

/** Is this one of the shells we know? Narrows a browser-supplied string. */
export function isClientPlatform(value: string | undefined): value is ClientPlatform {
  return !!value && (CLIENT_PLATFORMS as readonly string[]).includes(value);
}

/** Is this one of the operating systems we know? Narrows a browser-supplied string. */
export function isClientOs(value: string | undefined): value is ClientOs {
  return !!value && (CLIENT_OSES as readonly string[]).includes(value);
}

/**
 * Turn the operating-system name a user-agent parser produced into our own
 * vocabulary.
 *
 * The input is whatever Bowser returns — `Bowser.getParser(ua).getResult().os.name`
 * — which is the single source of truth the client already uses for the `$os`
 * property. Bowser has spelled macOS three different ways across versions
 * (`macOS`, `Mac OS`, `Mac OS X`), so all three are accepted; matching is
 * case-insensitive for the same reason.
 *
 * Anything unrecognised becomes `unknown`. That is deliberate: a name we have
 * not seen before must not be passed through into Mixpanel as a new bucket
 * nobody can explain a year from now.
 */
export function normalizeOsName(osName: string | undefined): ClientOs {
  switch ((osName || '').trim().toLowerCase()) {
    case 'ios': return 'ios';
    case 'android': return 'android';
    case 'windows': return 'windows';
    case 'macos':
    case 'mac os':
    case 'mac os x': return 'macos';
    case 'linux': return 'linux';
    default: return 'unknown';
  }
}

/** What `resolveClientEnvironment` needs to know about the running browser. */
export interface ClientEnvironmentInput {
  /** true inside the Capacitor native shell — the installed phone app. */
  isNative: boolean;
  /**
   * true for the installed phone app AND for a web app installed to the home
   * screen. `isAppStandalone()` reports true for both, which is why `isNative`
   * has to be checked first below.
   */
  isStandalone: boolean;
  /** the raw operating-system name from the user-agent parser. */
  osName: string | undefined;
}

/**
 * The browser's answer about itself, resolved in the browser because that is the
 * only place both facts are known.
 *
 * Order matters. `isStandalone` is true for the phone app as well as for an
 * installed web app, so the native check comes first — otherwise every phone-app
 * purchase would be filed as an installed web app.
 */
export function resolveClientEnvironment(input: ClientEnvironmentInput): ClientEnvironment {
  const os = normalizeOsName(input.osName);

  // if we are inside the phone app, the operating system names which one it is
  if (input.isNative) {
    if (os === 'ios') return { platform: 'ios-native', os };
    if (os === 'android') return { platform: 'android-native', os };
    // there is no third native shell today. Saying `unknown` is the honest answer
    // if one ever appears; picking either existing one would be a lie.
    return { platform: 'unknown', os };
  }

  // not the phone app, but installed to the home screen — the PWA
  if (input.isStandalone) {
    return { platform: 'installed-pwa', os };
  }

  // an ordinary browser tab. This also covers an in-app browser (Instagram,
  // Facebook), which is why the operating system beside it is load-bearing.
  return { platform: 'web-browser', os };
}

/** What `resolveSubscriptionClientEnvironment` needs to know about a purchase. */
export interface SubscriptionClientEnvironmentInput {
  /** which payment provider handled this subscription. */
  provider: 'apple' | 'googlePlay' | 'payPro' | 'paddle';
  /** what the web checkout reported, if anything. Browser-supplied, so untrusted. */
  checkoutPlatform?: string;
  /** what the web checkout reported, if anything. Browser-supplied, so untrusted. */
  checkoutOs?: string;
}

/**
 * The server's answer: what to record on a subscription event.
 *
 * For the two store providers the payment provider is PROOF, and nothing the
 * checkout said can overrule it — an Apple in-app purchase can only happen
 * inside the iPhone app, a Google Play purchase only inside the Android app. So
 * those two never consult the checkout at all, which also means they are correct
 * for every past and future store purchase with no client change whatsoever.
 *
 * For the two web providers the browser is the only source, and its values reach
 * us over the open internet inside the checkout blob. Each is therefore checked
 * against the known vocabulary and dropped to `unknown` if it does not match.
 * The two are checked independently, so one bad value does not cost us the
 * other.
 *
 * A web checkout that carried nothing — every link created before this shipped —
 * reports `unknown` rather than `web-browser`. Guessing `web-browser` would
 * invent a fact, and it would be wrong for every purchase made on a phone.
 */
export function resolveSubscriptionClientEnvironment(
  input: SubscriptionClientEnvironmentInput,
): ClientEnvironment {
  if (input.provider === 'apple') {
    return { platform: 'ios-native', os: 'ios' };
  }
  if (input.provider === 'googlePlay') {
    return { platform: 'android-native', os: 'android' };
  }

  return {
    platform: isClientPlatform(input.checkoutPlatform) ? input.checkoutPlatform : 'unknown',
    os: isClientOs(input.checkoutOs) ? input.checkoutOs : 'unknown',
  };
}
